import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { FeatureHelperService } from '../_helpers/feature-helper/feature-helper.service';
import { StudentFeedbackRequiredHelperService } from '../_helpers/student-feedback-required-helper/student-feedback-required-helper.service';
import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { BreadcrumbService } from '../_helpers/breadcrumb/breadcrumb.service';
import { BreadcrumbNode } from '../_models/breadcrumb/breadcrumb.model';
import { TutorReportsHelperService } from '../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { UtilityBarHelperService } from '../_helpers/utility-bar-helper/utility-bar-helper.service';
import { FeedbackSurvey } from '../_models/student-feedback/feedback-survey.model';
import { AuthenticationService } from '../_services';
import { SignalRService } from '../_services/signalR.service';
import { SpinnerService } from '../_services/spinner/spinner.service';
import { StudentFeedbackService } from '../_services/student-feedback/student-feedback.service';
import { VersionCheckService } from '../_services/version-check/version-check.service';

declare var window: any;

@Component({
    selector: 'app-app-new-base',
    templateUrl: './app-new-base.component.html',
    styleUrls: ['./app-new-base.component.css'],
    standalone: false
})
export class AppNewBaseComponent implements OnInit {
  @ViewChild('outlet', {static: false}) outlet: RouterOutlet;
  
  public links: Array<{ text: string, path: string }> = [];
  public utilityTemplate: TemplateRef<any>;
  
  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public signalRService: SignalRService,
    public themeService: ThemeService,
    public breadcrumbService: BreadcrumbService,
    public versionCheckService: VersionCheckService,
    private spinnerService: SpinnerService,
    private featureHelperService: FeatureHelperService,
    private feedbackService: StudentFeedbackService,
    private errorModalHelper: ErrorModalHelperService,
    private authService: AuthenticationService,
    private studentFeedbackHelper: StudentFeedbackRequiredHelperService,
    private utilityBarHelper: UtilityBarHelperService,
    private tutorReportsHelperService: TutorReportsHelperService
  ) {}

  /** Breadcrumb parents that live under a placement id (/tutorreports/:id, /view-sessions/:id). */
  private static readonly placementScopedRoutes = ['/tutorreports', '/view-sessions'];

  /**
   * Builds the routerLink array for a breadcrumb parent. Placement-scoped parents take the placement
   * id straight from the active child route (both the `:id` and `:placementId/:studentId` families)
   * and fall back to shared context only when the current route carries no id (e.g. the classic
   * add-session screen).
   */
  public breadcrumbLink(node: BreadcrumbNode): any[] {
    if (AppNewBaseComponent.placementScopedRoutes.includes(node.route)) {
      const params = this.route.firstChild?.snapshot.paramMap;
      const placementId =
        params?.get('id') ?? params?.get('placementId') ?? this.tutorReportsHelperService.commonData?.placementId;
      if (placementId != null && Number(placementId) > 0) {
        return [node.route, placementId];
      }
    }
    return [node.route];
  }

  ngOnInit() {

    setTimeout(() => {
      const parsedUrl = new URL(window.location.href);
      const urlHost = parsedUrl.host;

      // version.json is produced by the deploy pipeline, so it only exists on deployed sites —
      // skip the check when served locally (ng serve) where it would 404 on every poll.
      // (The old guard envName != "dev" was dead: no build config sets envName to exactly "dev".)
      const localHost = urlHost.startsWith("localhost") || urlHost.startsWith("127.0.0.1");
      if (!localHost) {
        this.versionCheckService.initVersionCheck(window.location.origin + "/version.json");
        console.log("version check has been completed");
      }

      let callApi = parsedUrl.pathname.indexOf("login") == -1 && parsedUrl.pathname != "/signmein" && parsedUrl.pathname != "/student-password-gen" && parsedUrl.pathname != "/student-feedback";
      this.themeService.setActiveTheme('LEAP', callApi);

      if (currentUserFeatures.filter(f => f.feature === "Student")[0].enabled) {
        if (parsedUrl.pathname == "/") {
          this.checkStudentPendingFeedback();
        }
      }
      
    }, 100);
    
    this.spinnerService.populateHints();

    let featureList = new Array<string>();
    featureList.push("Student");

    let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

    this.utilityBarHelper.utilityBerRefRefreshWatch().subscribe(refresh => {
      if (refresh && this.outlet.isActivated && this.outlet.component) {
        this.setUtilityReference(this.outlet.component);
      }
    })

  }

  public onRouterOutletActivate(componentInstance: any): void {
    this.setUtilityReference(componentInstance);
  }

  private checkStudentPendingFeedback(): void {
    this.feedbackService.checkUserFeedbackSurveyOnLogin(this.authService.userSessionData.currentUser.id).subscribe((result: FeedbackSurvey) => {
      if (result) {
        this.studentFeedbackHelper.showStudentFeedbackRequiredModal(result);
      }
    },
    error => {
      if (error.status === 401) {
        this.router.navigate(['/login']);
      }
      else {
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  private setUtilityReference(componentInstance: any): void {
    // Deferred to a zone-patched microtask (Promise.then, NOT queueMicrotask — zone.js does not patch
    // queueMicrotask, so the assignment would land outside Angular's zone): this is called from the
    // router-outlet (activate) event (and the refresh watch), which fire while change detection is
    // already running — assigning the template synchronously flips this component's [utilityTemplate]
    // binding after it was checked (NG0100).
    Promise.resolve().then(() => {
      if (componentInstance.childUtilityRefComponent) {
        this.utilityTemplate = componentInstance.childUtilityRefComponent.utilityBarForm;
      } else {
        this.utilityTemplate = componentInstance.utilityBarForm;
      }
    });
  }

}
